import { z } from "zod";
import type { CurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { dossierFilterOptionsSchema } from "@/dossiers/endOfContractFilter";

/**
 * The dossier overview configuration as served by
 * `GET /admin/dossiers/serve`: which KPI tiles and filters the affiliate
 * enabled, which columns the list shows and in what order, and how many
 * dossiers fit on a page.
 *
 * Parsed rather than typed straight off the generated client: the serve
 * operation documents no OpenAPI response schema, so generation would yield
 * `unknown`. Mirrors `dossierDetailLayout.ts`, which does the same for the
 * detail page.
 */

/** A KPI tile, matching a count returned by `/admin/dossiers/stats`. */
export const DOSSIER_OVERVIEW_KPIS = [
  "total",
  "unread",
  "mine",
  "actionRequired",
] as const;

/** A filter control, matching a field of the list's URL search state. */
export const DOSSIER_OVERVIEW_FILTERS = [
  "period",
  "servicePartner",
  "company",
  "assignee",
  // The team (RK-1399); gated on admin_user_collection_read in configurations.
  "userCollection",
  "status",
  "unread",
  "actionRequired",
  "archived",
  // The report filters (RK-1230): insurance, claimability and vehicle brand.
  // `brand` is permission-gated (admin_vehicle_brands).
  "externalInsured",
  "claimable",
  "brand",
  // Affiliate-specific; only offered by served layouts, never the fallback.
  "matchedRule",
  // The Schadeoorzaak (cause of damage) and Type (dossier type) filters.
  "causeOfDamage",
  "dossierType",
  // The Herstelmethode (repair type) filter.
  "repairType",
] as const;

/** A list column, matching an entry of the column registry in `dossierColumns.tsx`. */
export const DOSSIER_OVERVIEW_COLUMNS = [
  "licensePlate",
  "vehicle",
  "driver",
  "damageAt",
  "damageNumber",
  "damageFromDirection",
  "company",
  "servicePartner",
  // Same repair shop as `servicePartner`, but only its own name (without the
  // group name), under the header "Vestiging".
  "servicePartnerLocation",
  "dossierType",
  "createdAt",
  "assignee",
  "userCollection",
  "status",
  "hasImages",
] as const;

export type DossierOverviewKpi = (typeof DOSSIER_OVERVIEW_KPIS)[number];
export type DossierOverviewFilter = (typeof DOSSIER_OVERVIEW_FILTERS)[number];
export type DossierOverviewColumn = (typeof DOSSIER_OVERVIEW_COLUMNS)[number];

/**
 * Columns the admin dossier list can render a clickable sort header for, matching
 * `DossierOverviewColumn::orderProperty()` on the backend (only these have a
 * single Doctrine property the API can order by). Kept as its own list rather
 * than reusing {@link DOSSIER_OVERVIEW_COLUMNS}: adding a column to that list
 * (a new displayable column) must not silently make it look sortable here.
 */
export const DOSSIER_SORT_FIELDS = ["driver", "createdAt"] as const;
export type DossierSortField = (typeof DOSSIER_SORT_FIELDS)[number];
export type DossierSortDirection = "ASC" | "DESC";

/**
 * Which dossier overview columns render a clickable sort header, and the
 * column/direction the list starts sorted by. Absent entirely (see
 * {@link DossierOverviewLayout.sorting}) means no column is clickable and the
 * list keeps the collection's unconfigured order.
 */
export interface DossierOverviewSorting {
  readonly columns: readonly DossierSortField[];
  readonly defaultColumn: DossierSortField;
  readonly defaultDirection: DossierSortDirection;
}

/**
 * Drops identifiers this front-end does not know instead of rejecting the whole
 * payload, so a configuration written for a newer front-end degrades rather
 * than throwing the page back to its default. Duplicates are dropped too: the
 * backend rejects them, but a repeated column would render twice and break the
 * grid, which is worse than silently ignoring it.
 */
function knownValues<T extends string>(
  allowed: readonly T[],
): z.ZodType<T[], z.ZodTypeDef, unknown> {
  const allowedSet = new Set<string>(allowed);

  return z
    .array(z.string())
    .default([])
    .transform((values) => [
      ...new Set(values.filter((value): value is T => allowedSet.has(value))),
    ]);
}

export const dossierOverviewLayoutSchema = z.object({
  type: z.literal("dossierOverview"),
  // Bounded here as well as server-side: this value sizes both the request and
  // the skeleton, and a payload from an older backend must not be able to ask
  // for a page the collection endpoint will not serve.
  itemsPerPage: z.number().int().min(1).max(30),
  kpis: knownValues(DOSSIER_OVERVIEW_KPIS),
  filters: knownValues(DOSSIER_OVERVIEW_FILTERS),
  filterOptions: dossierFilterOptionsSchema,
  // An empty list is accepted: the server drops every column the current user
  // may not see, so a configuration whose columns are all permission-gated
  // legitimately resolves to none. Falling back to the default here would put
  // exactly those gated columns back, so the page renders an empty-layout
  // notice instead (see the dossiers route).
  columns: knownValues(DOSSIER_OVERVIEW_COLUMNS),
  // Optional for compatibility with existing configurations and older servers.
  // Degrades per entry like `columns`: an invalid label falls back to that
  // column's default header without discarding the other overrides.
  columnLabels: z
    .record(z.unknown())
    .optional()
    .catch(undefined)
    .transform((labels) => {
      if (labels === undefined) return undefined;
      const valid = Object.entries(labels).filter(
        (entry): entry is [string, string] =>
          typeof entry[1] === "string" &&
          entry[1].trim() !== "" &&
          [...entry[1]].length <= 255,
      );
      return valid.length === 0
        ? undefined
        : Object.fromEntries(valid.map(([id, label]) => [id, label.trim()]));
    }),
  // Absent by default (server omits it when the affiliate has not configured
  // sorting, see DossierOverviewLayoutResolver). Malformed as a whole rather
  // than degraded field-by-field like `columns` above: a `defaultColumn` this
  // front-end does not recognise (e.g. a newer backend adding a sortable
  // column) makes the whole block untrustworthy, since there is no safe
  // fallback default column to substitute. `.catch(undefined)` contains that
  // failure to this one field, so an otherwise-valid configuration still
  // renders its KPIs, filters and columns.
  sorting: z
    .object({
      columns: knownValues(DOSSIER_SORT_FIELDS),
      defaultColumn: z.enum(DOSSIER_SORT_FIELDS),
      defaultDirection: z.enum(["ASC", "DESC"]),
    })
    .optional()
    .catch(undefined),
});

export type DossierOverviewLayout = z.infer<typeof dossierOverviewLayoutSchema>;

/**
 * The configuration used when the affiliate has none (the serve endpoint
 * answers 404) or when the payload is not one this front-end understands. Any
 * other transport failure is an error, not a fallback. It is a copy of the
 * configuration shipped as the development fixture
 * (`backend/fixtures/dev/default/view_configurations/admin-dossier-overview.json`):
 * that one is affiliate data, this one is the behaviour the app falls back to
 * when an affiliate has none, and the two must render the same page for a user
 * holding every permission the fixture's entries are gated on (`security` is
 * evaluated and stripped server-side, so it never appears here).
 *
 * This is the unresolved default: it names every entry, including the ones the
 * fixture gates on a permission. Never render it as-is for a user — resolve it
 * with {@link resolveDefaultDossierOverviewLayout} first, which is the
 * client-side equivalent of the server's resolver.
 */
export const DEFAULT_DOSSIER_OVERVIEW_LAYOUT: DossierOverviewLayout = {
  type: "dossierOverview",
  itemsPerPage: 10,
  kpis: ["total", "unread", "mine"],
  filters: [
    "unread",
    "archived",
    "company",
    "servicePartner",
    "assignee",
    "period",
    "status",
    "externalInsured",
    "claimable",
    "brand",
  ],
  columns: [
    "licensePlate",
    "vehicle",
    "driver",
    "company",
    "servicePartner",
    "createdAt",
    "assignee",
    "status",
  ],
  // Absent, matching the fixture: sorting is opt-in per affiliate (see
  // `DossierOverviewSorting`), and only broekhuis-lease's configuration
  // currently opts in — the shipped default and dev fixtures deliberately do
  // not, so an affiliate with no configuration at all gets no clickable
  // headers and no `order[...]` parameter, exactly like one that configured
  // no sorting explicitly.
};

/**
 * The permission each default filter is gated on, mirroring the `security`
 * expressions of the fixture the default is a copy of. Filters absent here are
 * ungated and always shown.
 */
const DEFAULT_FILTER_PERMISSIONS: Partial<
  Record<DossierOverviewFilter, string>
> = {
  company: "admin_company_read",
  servicePartner: "admin_service_partner_read",
  assignee: "admin_user_read",
  actionRequired: "admin_work_queue_task_read",
  brand: "admin_vehicle_brands",
};

/** The permission each default column is gated on. See above. */
const DEFAULT_COLUMN_PERMISSIONS: Partial<
  Record<DossierOverviewColumn, string>
> = {
  company: "admin_company_read",
  servicePartner: "admin_service_partner_read",
  assignee: "admin_user_read",
};

/**
 * The default resolved for the given user: the client-side equivalent of the
 * server-side resolver, which drops the entries whose `security` the user does
 * not satisfy before serving a configuration.
 *
 * Without this, an affiliate that has no stored configuration (a 404) would
 * hand every user the unrestricted default, so a user holding only
 * dossier-read would get back the company, service-partner and assignee
 * filters and columns that a served configuration removes for them.
 */
export function resolveDefaultDossierOverviewLayout(
  user: CurrentUser,
): DossierOverviewLayout {
  return {
    ...DEFAULT_DOSSIER_OVERVIEW_LAYOUT,
    filters: DEFAULT_DOSSIER_OVERVIEW_LAYOUT.filters.filter((filter) =>
      isGranted(user, DEFAULT_FILTER_PERMISSIONS[filter]),
    ),
    columns: DEFAULT_DOSSIER_OVERVIEW_LAYOUT.columns.filter((column) =>
      isGranted(user, DEFAULT_COLUMN_PERMISSIONS[column]),
    ),
  };
}

/** An entry without a permission is ungated, so it always survives. */
function isGranted(user: CurrentUser, permission: string | undefined): boolean {
  return permission === undefined || hasPermission(user, permission);
}

/**
 * Parses a served payload, falling back to `fallback` when it is not a
 * configuration this front-end understands. A malformed configuration is
 * treated exactly like a missing one: the page keeps working instead of
 * failing on configuration the affiliate cannot fix from here.
 *
 * The fallback is passed in rather than defaulted to
 * {@link DEFAULT_DOSSIER_OVERVIEW_LAYOUT} so a fallback can never be less
 * restrictive than what the server would have served: callers hand in the
 * permission-resolved default (see
 * {@link resolveDefaultDossierOverviewLayout}).
 */
export function parseDossierOverviewLayout(
  payload: unknown,
  fallback: DossierOverviewLayout,
): DossierOverviewLayout {
  const result = dossierOverviewLayoutSchema.safeParse(payload);

  return result.success ? result.data : fallback;
}
